import { type ClassValue, clsx } from 'clsx';
import { format } from 'date-fns';
import { formatInTimeZone, toZonedTime } from 'date-fns-tz';
import { twMerge } from 'tailwind-merge';
import type { Article, Breadcrumb, Classification } from '@/app_lib/types/Article';
import type { ArticlePreview } from '@/app_lib/types/ArticlePreview';
import type { DestinationDescription } from '@/app_lib/types/Destination';
import type { MenuItem } from '@/app_lib/types/Menu';
import type { NestedMenuItem } from '@/app_lib/types/NestedMenuItem';
import type { PianoPageConfig } from '@/app_lib/types/Piano';
import { noAdArticles } from '@/appconstants';

/**
 * Replaces space in the middle of timestamp string, if it exists, with a 'T'.
 * Appends 'Z' to indicate UTC timezone if not already present.
 *
 * @param timestamp 'YYYY-MM-DD 00:00:00' | 'YYYY-MM-DD 00:00:00Z' | 'YYYY-MM-DDT00:00:00Z'
 * @returns string 'YYYY-MM-DDT00:00:00Z'
 */
const standardizeTimestamp = (timestamp: string): string => {
  if (!timestamp) {
    return '';
  }
  // Replace spaces with 'T'
  let standardized = timestamp.replace(/ /g, 'T');
  // Append 'Z' if timestamp doesn't already have a timezone indicator (Z or +/-HH:MM or +/-HHMM)
  // Check for 'Z' at the end, or timezone offset pattern like +05:00, -04:00, +0500, -0400
  if (!(standardized.endsWith('Z') || standardized.match(/[+-]\d{2}:?\d{2}$/))) {
    standardized += 'Z';
  }
  return standardized;
};

export const dayOfWeek = (): string => {
  const weekday = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const today = new Date();

  return weekday[today.getDay()];
};

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function extractHtml(htmlString: string) {
  return htmlString.replace(/<\/?[^>]+(>|$)/g, '').trim();
}

export const formatDate = (dateTimeString: string) => {
  const date = new Date(dateTimeString);

  return new Intl.DateTimeFormat('en-US', {
    day: 'numeric',
    month: 'long',
    timeZone: 'America/New_York',
    year: 'numeric',
  }).format(date);
};

export const getTimeDifference = (date: Date, updated: Date) => {
  const diffMs = Math.abs(date.getTime() - updated.getTime());

  const diffMinutes = diffMs / (1000 * 60);
  const diffHours = diffMinutes / 60;
  const diffDays = diffHours / 24;
  const diffMonths = diffDays / 30.44;
  const diffYears = diffDays / 365.25;

  if (diffMinutes < 60) {
    const minutes = Math.floor(diffMinutes);
    return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
  }
  if (diffHours < 24) {
    const hours = Math.floor(diffHours);
    return `${hours} hour${hours !== 1 ? 's' : ''}`;
  }
  if (diffDays < 30) {
    const days = Math.floor(diffDays);
    return `${days} day${days !== 1 ? 's' : ''}`;
  }
  if (diffMonths < 12) {
    if (diffMonths < 1) {
      return '1 month';
    }
    const months = Math.floor(diffMonths);
    return `${months} month${months !== 1 ? 's' : ''}`;
  }
  const years = Math.floor(diffYears);
  return `${years} year${years !== 1 ? 's' : ''}`;
};

export type DateTimeFormat = 'date' | 'datetime' | 'numeric' | 'compactNumeric';

/**
 * Standardizes timestamp string and then formats it to display readable
 * date or date and time or numeric date formats based on fmt param.
 * All dates are displayed in Eastern Time (America/New_York) for consistency.
 *
 * @param timestamp 'YYYY-MM-DD 00:00:00Z' | 'YYYY-MM-DDT00:00:00Z'
 * @param fmt 'datetime' | 'date' | 'numeric' | 'compactNumeric'
 * @returns '00 Month, YYYY, 00:00 AM/PM EST' | '00 Month, YYYY' | 'MM/DD/YY' | 'M/D/YYYY'
 */
export const formatDateTime = (timestamp: string, fmt: DateTimeFormat = 'datetime'): string => {
  let formattedTimeStamp = '';
  try {
    const standardizedStamp = standardizeTimestamp(timestamp);
    const utcDate = new Date(standardizedStamp);
    const timeZone = 'America/New_York';

    // Use date-fns-tz for timezone-aware formatting
    switch (fmt) {
      case 'datetime':
        // Format: "June 11, 2024, 10:30 AM ET"
        // Use formatInTimeZone to convert UTC to Eastern Time (handles DST automatically)
        // Always display "ET" instead of "EDT" or "EST"
        formattedTimeStamp = formatInTimeZone(utcDate, timeZone, 'MMMM d, yyyy, h:mm a') + ' ET';
        break;
      case 'date': {
        // Format: "June 11, 2024"
        // Convert to zoned time first, then format (no timezone abbreviation needed)
        const zonedDate = toZonedTime(utcDate, timeZone);
        formattedTimeStamp = format(zonedDate, 'MMMM d, yyyy');
        break;
      }
      case 'compactNumeric': {
        // Format: "6/11/24"
        const zonedDateCompact = toZonedTime(utcDate, timeZone);
        formattedTimeStamp = format(zonedDateCompact, 'M/d/yy');
        break;
      }
      case 'numeric': {
        // Format: "06/11/2024"
        const zonedDateNumeric = toZonedTime(utcDate, timeZone);
        formattedTimeStamp = format(zonedDateNumeric, 'MM/dd/yyyy');
        break;
      }
      default:
        break;
    }
  } catch (_error) {
    // TODO: Logger
    // console.error('Error formatting timestamp:', error);
  }
  return formattedTimeStamp;
};

export function wpImageUtils(url: string, width: number, height: number) {
  try {
    const baseUrl = new URL(url);
    baseUrl.searchParams.delete('resize');
    const separator = baseUrl.search ? '&' : '?';
    return `${baseUrl.toString()}${separator}resize=${width},${height}`;
  } catch (_e) {
    // biome-ignore lint/suspicious/noConsole: need error
    console.error('Error parsing URL:', _e);
    return '/logos/fallback.png';
  }
}

export function getCurrentHost(): string | undefined {
  // Client-side: use window.location
  if (typeof window !== 'undefined') {
    return window.location.host;
  }

  // Server-side: use environment variable
  if (process.env.SITE_BASE_URL) {
    return new URL(process.env.SITE_BASE_URL).host;
  }

  // Fallback for development/other environments to nextjs convention
  return process.env.HOSTNAME;
}

export function wpImageUrlBuilder(url: string | undefined) {
  if (!url) {
    return '/logos/fallback.png';
  }
  // ast-grep-ignore: no-absolute-fortune-urls
  const FortuneAssetUrl = 'https://fortune.com/img-assets/wp-content/uploads/';
  const baseUrl = new URL(url, FortuneAssetUrl);
  return baseUrl.toString();
}

export const defaultZipCode = '92014';

export function isValidUsZipCode(value: string) {
  return /^([0-9]{5})(?:-([0-9]{4}))?$/.test(value);
}

// https://regex101.com/library/TqLf63
export function isValidCaPostalCode(value: string) {
  return /^[ABCEGHJ-NPRSTVXY][0-9][ABCEGHJ-NPRSTV-Z][ ]?[0-9][ABCEGHJ-NPRSTV-Z][0-9]$/i.test(value);
}

export const getUserZipCode = async () => {
  try {
    // fortune.com/geolocation has `Access-Control-Allow-Origin: *` so it can be called from any internal domains & localhost
    // ast-grep-ignore: no-absolute-fortune-urls
    const response = await fetch('https://fortune.com/geolocation/');
    const zipCode = response.headers.get('geoip_postal_code');
    return zipCode && isValidUsZipCode(zipCode) ? zipCode : defaultZipCode;
  } catch {
    return defaultZipCode;
  }
};

export const isValidEmail = (str: string): boolean => {
  const pattern = /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/;
  return Boolean(pattern.test(str));
};

// This is a generalized regex for possible international zip codes.
// It allows alphanumeric characters, hyphens, and spaces, and requires a maximum of 10 characters, minimum of 3, with a letter or number at the start and end.
export const isValidInternationalPostalCode = (value: string) => {
  return /^[a-z0-9][a-z0-9\- ]{1,8}[a-z0-9]$/i.test(value);
};

export const isArticlePagePath = (pathname: string) => {
  return (
    /\/\d{4}\/\d{2}\/\d{2}\/[^/]+\//.test(pathname) || // /YYYY/MM/DD/SLUG/
    /\/article\/[^/]+\//.test(pathname) || // /article/SLUG/
    /\/education\/[^/]+\/[^/]+\//.test(pathname) || // /education/SUBSECTION/SLUG/
    /\/([^/]{3,})\/\d{4}\/\d{2}\/\d{2}\/[^/]+\//.test(pathname) || // /SECTION/YYYY/MM/DD/SLUG/
    /\/([^/]{3,})\/article\/[^/]+\//.test(pathname) // /SECTION/article/SLUG/
  );
};

export const getEducationBreadcrumbs = ({
  classification,
  description,
  title,
  section,
}: {
  classification: Classification;
  description: DestinationDescription;
  title: string;
  section: string;
}) => {
  const items: Breadcrumb[] = [
    {
      href: '/education',
      label: 'education',
    },
  ];

  const primarySection = classification.primarySection;
  const sections = classification.sections || [];
  const secondarySection = sections.find(
    (candidateSection) => candidateSection.sectionId !== primarySection?.sectionId,
  );

  if (secondarySection && description.sections) {
    const breadcrumbsNewSection = Object.values(description.sections || {})?.find(
      (candidateSection) => candidateSection.slug === secondarySection.slug,
    );
    if (breadcrumbsNewSection) {
      items.push({
        href: `/education/${breadcrumbsNewSection.slug.replace('edu_', '')}`,
        label: breadcrumbsNewSection.name,
      });
    }
  }

  // failed to find the primary or secondary section, so add a default
  if (items.length === 1) {
    items.push({
      href: primarySection?.slug ? `/education/${primarySection.slug.replace('edu_', '')}` : `/education/${section}`,
      label: primarySection?.name || 'Education Articles',
    });
  }

  items.push({
    label: title,
  });
  return items;
};

export function isPaywallContent(pageConfig: PianoPageConfig): boolean {
  const paywallExemptTags = ['Always Free', ...noAdArticles];

  return (
    (pageConfig.contentSection.startsWith('P') ||
      ['F-BreakingNews', 'F-Conference', 'F-Search', 'F-Service', 'F-Wire', 'F-Commentary', 'legacy'].includes(
        pageConfig.contentSection,
      )) &&
    paywallExemptTags.every((tag) => !pageConfig.tags.includes(tag))
  );
}

export function parseTopics(topics: string[]): string[] {
  // remove non alphanumeric characters and convert to lowercase
  const parsedTopics: string[] = topics.map((topic) => topic.toLowerCase().replaceAll(/[^a-zA-Z0-9]/g, ''));

  return parsedTopics;
}

export function parseAdTags(adTags: { name: string; description?: string; slug: string }[]): string[] {
  if (!adTags) {
    return [];
  }

  const parsedTags: string[] = adTags.map((tag) => tag.slug.toLowerCase());

  return parsedTags;
}

export const isTrinityAdless = (article: Article) => {
  const sponsoredContent = {
    adTags: ['No Trinity Ads'],
    tags: ['Applecustom,', 'Apple-custom', 'Apple custom', 'brainstormus'],
    topics: [
      'leadershipnext',
      'brainstormaieurope',
      'bestcompaniesworkfor',
      'global500',
      'cfodaily',
      'cfo-daily',
      '100bestcompaniestoworkfor',
      'Brainstormaiasia',
      'fortune500',
      'bestcompanies',
      'chrodaily',
      'chro daily',
      'southeastasia500',
      'next-to-lead-newsletter',
      'Next to Lead newsletter',
      'Applecustom',
      'Apple-custom',
      'Apple custom',
      'brainstormus',
      'brainstormaius',
    ],
  };

  const sponsoredConditions: string[] = [];
  const articleConditions: string[] = [];

  Object.keys(sponsoredContent).forEach((key) =>
    sponsoredConditions.push(
      ...parseTopics(sponsoredContent[key as keyof typeof sponsoredContent]).map((string) =>
        string.replaceAll(' ', ''),
      ),
    ),
  );

  const tagNames = article.tags.nodes.map((tag) => tag.name);
  const topicNames = article.topics.map((topic) => topic.name);
  const adTagNames = article.adTags.edges.map((adTag) => adTag.node.name);

  parseTopics(tagNames).forEach((tagName) => articleConditions.push(tagName.split(' ').join('')));
  parseTopics(topicNames).forEach((topicName) => articleConditions.push(topicName.split(' ').join('')));
  parseTopics(Array.from(adTagNames)).forEach((adTagName) => articleConditions.push(adTagName.split(' ').join('')));

  return articleConditions.some((articleCondition) => sponsoredConditions.includes(articleCondition));
};

export function buildMenuTree(menuItems: MenuItem[]) {
  const menuTree: Record<string, NestedMenuItem> = {};
  let sectionsId = '';

  menuItems.forEach((menuItem) => {
    if (menuItem.label === 'Sections') {
      sectionsId = menuItem.id;
    } else if (menuItem.parentId === null || menuItem.parentId === sectionsId) {
      menuTree[menuItem.id] = {
        id: menuItem.id,
        label: menuItem.label,
        subMenu: [] as MenuItem[],
        url: menuItem.url,
      };
    } else if (menuTree[menuItem.parentId]) {
      menuTree[menuItem.parentId].subMenu?.push(menuItem);
    }
  });

  return Object.values(menuTree);
}

export const getPrimarySection = ({
  primarySectionId,
  sections,
}: Pick<ArticlePreview, 'primarySectionId' | 'sections'>) => {
  primarySectionId = primarySectionId.replace(/^"|"$/g, '');

  // TODO: Is it okay to default to the first section like this?
  return sections.nodes.find((section) => section.databaseId === Number(primarySectionId)) ?? sections.nodes[0];
};

export const getLocalIsoDateTime = (dateTime: string) => {
  try {
    if (dateTime === '') {
      return '';
    }

    // getTimezoneOffset returns minutes *behind* UTC.
    // For example: EDT (-04:00) → offset = 240
    const offset = new Date().getTimezoneOffset();
    const absO = Math.abs(offset);

    const sign = offset < 0 ? '+' : '-'; // flip logic
    const hours = Math.floor(absO / 60)
      .toString()
      .padStart(2, '0');
    const minutes = (absO % 60).toString().padStart(2, '0');

    const zone = `${sign}${hours}:${minutes}`;
    return dateTime + zone;
  } catch {
    return '';
  }
};

/**
 * Generates a RFC4122 version 4 UUID string
 * Matches the server-side implementation in middleware
 */
export function generateUuid(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}
